import React, { useState } from 'react';
import { Save, Play, Trash2, Download, Upload, Plus, Moon, Sun, Zap, Settings, HelpCircle, Command, List } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import useFlowStore from '../store/flowStore';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import * as Tooltip from '@radix-ui/react-tooltip';

interface ModernToolbarProps {
  onShowSettings?: () => void;
  onShowHelp?: () => void;
  onShowShortcuts?: () => void;
}

const ModernToolbar: React.FC<ModernToolbarProps> = ({ 
  onShowSettings,
  onShowHelp,
  onShowShortcuts
}) => {
  const { 
    nodes, 
    edges, 
    flows,
    currentFlowId,
    testMode,
    saveFlow,
    setTestMode,
    testFlow 
  } = useFlowStore();
  
  const { isDark, toggleTheme } = useTheme();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);

  const handleSave = async () => {
    if (!flowName.trim()) {
      toast.error('Inserisci un nome per l\'automazione');
      return;
    }
    
    await saveFlow(flowName, flowDescription);
    toast.success(currentFlowId ? 'Automazione aggiornata con successo!' : 'Automazione salvata con successo!');
    setShowSaveDialog(false);
    setFlowName('');
    setFlowDescription('');
  };

  const handleTest = () => {
    if (nodes.length === 0) {
      toast.error('Aggiungi almeno un nodo per testare l\'automazione');
      return;
    }
    
    setTestMode(!testMode);
    setIsExecuting(!testMode);
    
    if (!testMode) {
      toast.success('Modalità test attivata', { icon: '🚀' });
      testFlow();
      
      // Simulate execution
      setTimeout(() => {
        setIsExecuting(false);
        toast.success('Test completato!', { icon: '✅' });
      }, 3000);
    } else {
      toast('Modalità test disattivata', { icon: 'ℹ️' });
    }
  };

  const handleClear = () => {
    if (window.confirm('Sei sicuro di voler cancellare tutto il flow?')) {
      useFlowStore.setState({ nodes: [], edges: [] });
      toast.success('Flow cancellato');
    }
  };

  const handleExport = () => {
    const data = {
      nodes,
      edges,
      exportedAt: new Date().toISOString(),
      version: '2.0'
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-flow-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Flow esportato con successo');
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target?.result as string);
          if (data.nodes && data.edges) {
            useFlowStore.setState({ nodes: data.nodes, edges: data.edges });
            toast.success('Flow importato con successo');
          } else {
            toast.error('File non valido');
          }
        } catch (error) {
          toast.error('Errore durante l\'importazione del file');
        }
      };
      reader.readAsText(file);
    };
    
    input.click();
  };

  interface ToolbarButtonProps {
    icon: React.ElementType;
    label: string;
    onClick: () => void;
    variant?: 'default' | 'primary' | 'danger';
    disabled?: boolean;
    shortcut?: string;
  }

  const ToolbarButton: React.FC<ToolbarButtonProps> = ({ icon: Icon, label, onClick, variant = 'default', disabled = false, shortcut }) => (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClick}
          disabled={disabled}
          className={`
            relative p-2.5 rounded-xl transition-all duration-200
            ${variant === 'primary' 
              ? 'bg-gradient-to-r from-whatsapp-green to-whatsapp-blue text-white shadow-lg hover:shadow-xl' 
              : variant === 'danger'
              ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <Icon className="w-5 h-5" />
          {shortcut && (
            <kbd className="absolute -bottom-1 -right-1 text-[10px] bg-gray-200 dark:bg-gray-700 px-1 rounded">
              {shortcut}
            </kbd>
          )}
        </motion.button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-xl"
          sideOffset={5}
        >
          {label}
          <Tooltip.Arrow className="fill-gray-900" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );

  return (
    <Tooltip.Provider delayDuration={0}>
      <motion.div 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md shadow-elegant border-b border-gray-200 dark:border-dark-border"
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center gap-4">
              <motion.div
                animate={{ rotate: testMode ? 360 : 0 }}
                transition={{ duration: 2, repeat: testMode ? Infinity : 0, ease: "linear" }}
                className="text-3xl"
              >
                <Zap className="w-8 h-8 text-whatsapp-green" />
              </motion.div>
              <div>
                <h1 className="text-xl font-bold gradient-text">WhatsApp Flow</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Automation Builder v2.0
                </p>
              </div>
              {currentFlowId && (
                <div className="ml-4 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Flow #{currentFlowId}
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Primary Actions */}
              <div className="flex items-center gap-2 mr-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    // Pre-populate name/description if editing existing flow
                    if (currentFlowId) {
                      const existingFlow = flows.find(f => f.id === currentFlowId);
                      if (existingFlow) {
                        setFlowName(existingFlow.name);
                        setFlowDescription(existingFlow.description || '');
                      }
                    }
                    setShowSaveDialog(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  <Save className="w-4 h-4" />
                  <span className="font-medium">Salva</span>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleTest}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all
                    ${testMode 
                      ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white' 
                      : 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white'
                    }
                  `}
                >
                  <Play className={`w-4 h-4 ${isExecuting ? 'animate-pulse' : ''}`} />
                  <span className="font-medium">
                    {testMode ? 'Stop' : 'Test'}
                  </span>
                </motion.button>

                <Link
                  to="/flows"
                  className="relative flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors group"
                  title="Gestisci Flow (⌘L)"
                >
                  <List className="w-5 h-5" />
                  <span className="font-medium">Gestisci Flow</span>
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    ⌘L
                  </span>
                </Link>
              </div>

              {/* Separator */}
              <div className="w-px h-8 bg-gray-300 dark:bg-gray-700" />

              {/* Secondary Actions */}
              <div className="flex items-center gap-1">
                <ToolbarButton
                  icon={Download}
                  label="Esporta"
                  onClick={handleExport}
                  shortcut="⌘E"
                />
                <ToolbarButton
                  icon={Upload}
                  label="Importa"
                  onClick={handleImport}
                  shortcut="⌘I"
                />
                <ToolbarButton
                  icon={Trash2}
                  label="Cancella tutto"
                  onClick={handleClear}
                  variant="danger"
                />
              </div>

              {/* Separator */}
              <div className="w-px h-8 bg-gray-300 dark:bg-gray-700" />

              {/* Utility Actions */}
              <div className="flex items-center gap-1">
                <ToolbarButton
                  icon={Command}
                  label="Scorciatoie"
                  onClick={onShowShortcuts || (() => {})}
                />
                <ToolbarButton
                  icon={HelpCircle}
                  label="Aiuto"
                  onClick={onShowHelp || (() => {})}
                />
                <ToolbarButton
                  icon={Settings}
                  label="Impostazioni"
                  onClick={onShowSettings || (() => {})}
                />
                <ToolbarButton
                  icon={isDark ? Sun : Moon}
                  label={isDark ? 'Modalità chiara' : 'Modalità scura'}
                  onClick={toggleTheme}
                />
              </div>
            </div>
          </div>

          {/* Status Bar */}
          <AnimatePresence>
            {(nodes.length > 0 || edges.length > 0) && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800"
              >
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-4">
                    <span>
                      <span className="font-medium">{nodes.length}</span> nodi
                    </span>
                    <span>
                      <span className="font-medium">{edges.length}</span> connessioni
                    </span>
                  </div>
                  {testMode && (
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span>Modalità test attiva</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </motion.div>

      {/* Save Dialog - Moved outside to ensure visibility */}
      {showSaveDialog && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          style={{ zIndex: 999999 }}
          onClick={() => setShowSaveDialog(false)}
        >
          <div
            className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl max-w-md w-full mx-4"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-xl font-bold gradient-text">
                Salva Automazione
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Dai un nome alla tua automazione per salvarla
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-xl focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50 transition-all"
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                  placeholder="es. Benvenuto nuovi utenti"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrizione (opzionale)
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-xl focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50 transition-all resize-none"
                  rows={3}
                  value={flowDescription}
                  onChange={(e) => setFlowDescription(e.target.value)}
                  placeholder="Descrivi cosa fa questa automazione..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg rounded-b-2xl">
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  setFlowName('');
                  setFlowDescription('');
                }}
                className="flex-1 px-4 py-2.5 text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2.5 text-white bg-gradient-to-r from-whatsapp-green to-emerald-600 rounded-xl hover:shadow-lg transition-all"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </Tooltip.Provider>
  );
};

export default ModernToolbar;
